import { useState, useCallback, useMemo, useRef } from 'react';
import { analytics } from '@eti/utils';
import { getSegmentIndex, getNextAvailableSegment, getPreviousAvailableSegment, getNextSegment, isFirstAvailableSegment, isLastAvailableSegment, isSeatPreferenceSegment } from '../utils/segmentUtils';
import { getAvailableDeckOptionsForSegment, getDefaultDeck, getDeckOptionForDeckType, getDeckType } from '../utils/deckUtils';
import { getNonSelectedAvailableSegments, getSegmentSeatSelections } from '../utils/travelerUtils';
import { focusOnActiveSeatMapTravelerButton, focusSelectedSeat } from '../utils/focusUtils';
import { useSeatMapAutoAdvance } from './useSeatMapAutoAdvance';
import { modalTypes, modalRedirectDestinations } from '../constants/modal';
export const Direction = {
  Forward: 1,
  Backward: -1
};
export const useSeatMapNavigation = ({
  deckSelectionsBySegment,
  flattenedBounds,
  getSelected,
  isManualNavigationEnabled,
  isSummarySegmentEnabled = true,
  isSummaryShowing,
  onDeckSelectionChange,
  onShowConfirmationModal,
  onSelectionComplete,
  selectedSeats,
  setIsSummaryShowing,
  t,
  travelers,
  usePersuasionConfiguration
}) => {
  var _firstAvailableSegmen, _firstAvailableSegmen2, _firstAvailableSegmen3;
  const deckLabel = useMemo(() => ({
    lower: t('Product.SeatMap.DeckSelection.LowerDeck'),
    main: t('Product.SeatMap.DeckSelection.MainDeck'),
    upper: t('Product.SeatMap.DeckSelection.UpperDeck')
  }), [t]);
  const getDeckOptions = useCallback(index => getAvailableDeckOptionsForSegment(flattenedBounds, index, deckLabel.upper, deckLabel.main, deckLabel.lower), [flattenedBounds, deckLabel]);
  const getDeckForSegment = useCallback(boundSegmentIndex => {
    var _getDeckOptions;
    const options = (_getDeckOptions = getDeckOptions(boundSegmentIndex)) !== null && _getDeckOptions !== void 0 ? _getDeckOptions : [];
    const rememberedDeck = deckSelectionsBySegment[boundSegmentIndex];
    const isRememberedDeckAvailable = options.some(option => option.value === (rememberedDeck === null || rememberedDeck === void 0 ? void 0 : rememberedDeck.value));
    return isRememberedDeckAvailable ? rememberedDeck : getDefaultDeck(options);
  }, [deckSelectionsBySegment, getDeckOptions]);
  const firstAvailableSegment = getNextSegment(flattenedBounds);
  const [activeSegment, setActiveSegment] = useState((_firstAvailableSegmen = firstAvailableSegment === null || firstAvailableSegment === void 0 ? void 0 : firstAvailableSegment.boundSegmentIndex) !== null && _firstAvailableSegmen !== void 0 ? _firstAvailableSegmen : '0');
  const [activeTraveler, setActiveTraveler] = useState((_firstAvailableSegmen2 = firstAvailableSegment === null || firstAvailableSegment === void 0 || (_firstAvailableSegmen3 = firstAvailableSegment.travelerIds) === null || _firstAvailableSegmen3 === void 0 ? void 0 : _firstAvailableSegmen3[0]) !== null && _firstAvailableSegmen2 !== void 0 ? _firstAvailableSegmen2 : 0);
  const [direction, setDirection] = useState(Direction.Forward);
  const [activeDeck, setActiveDeck] = useState(() => {
    var _firstAvailableSegmen4;
    return getDeckForSegment((_firstAvailableSegmen4 = firstAvailableSegment === null || firstAvailableSegment === void 0 ? void 0 : firstAvailableSegment.boundSegmentIndex) !== null && _firstAvailableSegmen4 !== void 0 ? _firstAvailableSegmen4 : '');
  });
  const [fromSummary, setFromSummary] = useState(false);
  const pendingSeatFocusRef = useRef(null);
  const handleExitComplete = () => {
    const pendingSeatFocus = pendingSeatFocusRef.current;
    pendingSeatFocusRef.current = null;
    if (pendingSeatFocus) {
      requestAnimationFrame(() => {
        focusSelectedSeat(pendingSeatFocus.row, pendingSeatFocus.value);
      });
    }
  };
  const foundSegment = flattenedBounds.find(s => s.boundSegmentIndex === activeSegment);
  const isFirstSegment = isFirstAvailableSegment(flattenedBounds, activeSegment);
  const isLastSegment = isLastAvailableSegment(flattenedBounds, activeSegment);
  const isSeatPreferenceAvailable = isSeatPreferenceSegment(foundSegment);
  const totalSelectedSeats = getSegmentSeatSelections(selectedSeats);
  const nonSelectedAvailableSegments = getNonSelectedAvailableSegments(flattenedBounds, totalSelectedSeats);
  const allSeatsSelected = (nonSelectedAvailableSegments || []).length === 0;
  const canTriggerUnbookedSegmentsDialog = usePersuasionConfiguration && isManualNavigationEnabled && isLastSegment && !allSeatsSelected;
  const handleDeckChange = option => {
    setActiveDeck(option);
    onDeckSelectionChange(activeSegment, option);
  };
  const handleSegmentChange = useCallback(segment => {
    var _travelerIds$;
    const {
      boundSegmentIndex,
      travelerIds
    } = segment;
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'SEGMENTS'
    }, {
      event: 'seatingVisible',
      seatingCurrentSegment: getSegmentIndex(flattenedBounds, boundSegmentIndex) + 1
    });
    const firstTraveler = (_travelerIds$ = travelerIds === null || travelerIds === void 0 ? void 0 : travelerIds[0]) !== null && _travelerIds$ !== void 0 ? _travelerIds$ : 0;
    setActiveDeck(getDeckForSegment(boundSegmentIndex));
    setActiveSegment(boundSegmentIndex);
    setActiveTraveler(firstTraveler);
    setIsSummaryShowing(false);
    setFromSummary(false);
    focusOnActiveSeatMapTravelerButton(firstTraveler);
  }, [flattenedBounds, getDeckForSegment, setIsSummaryShowing]);
  const changeSegmentWithDirection = useCallback((segment, dir) => {
    setDirection(dir);
    requestAnimationFrame(() => {
      handleSegmentChange(segment);
    });
  }, [handleSegmentChange]);
  const handleOnPreviousSegmentClick = useCallback(() => {
    if (isFirstAvailableSegment(flattenedBounds, activeSegment)) {
      return;
    }
    if (isSummaryShowing) {
      setIsSummaryShowing(false);
      setFromSummary(false);
      return;
    }
    const previousSegment = getPreviousAvailableSegment(flattenedBounds, activeSegment);
    if (previousSegment) {
      changeSegmentWithDirection(previousSegment, Direction.Backward);
    }
  }, [activeSegment, changeSegmentWithDirection, flattenedBounds, isSummaryShowing, setIsSummaryShowing]);
  const handleChangeTraveler = traveler => {
    setActiveTraveler(traveler);
    focusOnActiveSeatMapTravelerButton(traveler);
    const travelerSeat = getSelected(activeSegment).find(seat => seat.id === traveler);
    const seatDeckOption = getDeckOptionForDeckType(travelerSeat === null || travelerSeat === void 0 ? void 0 : travelerSeat.deckType, getDeckOptions(activeSegment));
    if (seatDeckOption && travelerSeat) {
      setActiveDeck(seatDeckOption);
      onDeckSelectionChange(activeSegment, seatDeckOption);
      requestAnimationFrame(() => {
        focusSelectedSeat(travelerSeat.row, travelerSeat.value);
      });
    }
    if (fromSummary) {
      setFromSummary(false);
    }
  };
  const handleOnNextSegmentClick = useCallback(() => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'CONTINUE'
    });
    if (isSummaryShowing) {
      return;
    }
    if (!isLastSegment) {
      const nextSegment = getNextAvailableSegment(flattenedBounds, activeSegment);
      if (nextSegment) {
        changeSegmentWithDirection(nextSegment, Direction.Forward);
      }
      return;
    }
    if (canTriggerUnbookedSegmentsDialog) {
      onShowConfirmationModal(modalTypes.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS, modalRedirectDestinations.SUMMARY);
      return;
    }
    if (!isSummarySegmentEnabled) {
      onSelectionComplete === null || onSelectionComplete === void 0 || onSelectionComplete();
      return;
    }
    setIsSummaryShowing(true);
    setFromSummary(false);
  }, [activeSegment, canTriggerUnbookedSegmentsDialog, changeSegmentWithDirection, flattenedBounds, isLastSegment, isSummarySegmentEnabled, isSummaryShowing, onShowConfirmationModal, onSelectionComplete, setIsSummaryShowing]);
  const handleToggleSeatSelect = (boundSegmentIndex, traveler, deckType) => {
    var _getSelected$find;
    setActiveSegment(boundSegmentIndex);
    setActiveTraveler(traveler);
    const seatDeckOption = getDeckOptionForDeckType(deckType, getDeckOptions(boundSegmentIndex));
    const activeDeck = seatDeckOption !== null && seatDeckOption !== void 0 ? seatDeckOption : getDeckForSegment(boundSegmentIndex);
    if (activeDeck) {
      setActiveDeck(activeDeck);
      onDeckSelectionChange(boundSegmentIndex, activeDeck);
    }
    pendingSeatFocusRef.current = (_getSelected$find = getSelected(boundSegmentIndex).find(seat => seat.id === traveler)) !== null && _getSelected$find !== void 0 ? _getSelected$find : null;
    setIsSummaryShowing(false);
    setFromSummary(true);
  };
  const deckOptions = getDeckOptions(activeSegment);
  const activeDeckType = getDeckType(activeDeck);
  useSeatMapAutoAdvance({
    activeSegment,
    activeTraveler,
    flattenedBounds,
    foundSegment,
    fromSummary,
    getSelected,
    isManualNavigationEnabled,
    isSeatPreferenceAvailable,
    selectedSeats,
    travelers,
    t,
    onChangeTraveler: handleChangeTraveler,
    onNextSegment: handleOnNextSegmentClick
  });
  return {
    activeSegment,
    activeTraveler,
    activeDeck,
    activeDeckType,
    canTriggerUnbookedSegmentsDialog,
    deckOptions,
    direction,
    isFirstSegment,
    isLastSegment,
    foundSegment,
    isSeatPreferenceAvailable,
    nonSelectedAvailableSegments,
    allSeatsSelected,
    fromSummary,
    totalSelectedSeats,
    setFromSummary,
    handleDeckChange,
    handleOnPreviousSegmentClick,
    handleOnNextSegmentClick,
    handleChangeTraveler,
    handleToggleSeatSelect,
    handleExitComplete
  };
};
//# sourceMappingURL=useSeatMapNavigation.js.map